<template>
    <div class="container">
        <v-title :breadcrumb="{label: `Create Order for ${customer.displayName}`, route: {name: 'add-order', params: {customerId}}}">
            Create Order for {{ customer.displayName }}
            <div slot="actions">
                <router-link :disabled="isLoading"
                    class="btn inline-s" :to="{name: 'customer-details', params: {id: customerId}}">
                    Cancel
                </router-link>
                <r-button type="primary" :loading="isLoading" @click="save">Save Order</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.$error"
            :server-error="serverError"
            :predicates="validationPredicates"
            class="stack-l"/>
        <template v-if="hasMultipleWebsites">
            <v-form-tile>
                <h2 slot="title">Select Website</h2>
                <div slot="content" class="tile">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <v-select
                                    label="Select Website"
                                    :auto-focus="hasMultipleWebsites"
                                    v-model="order.websiteId"
                                    :options="websiteList"
                                    :preselect-first="true"
                                    placeholder="Select a website"
                                ></v-select>
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
            <div class="divider"></div>
        </template>
        <v-form-tile>
            <h2 slot="title">Order type</h2>
            <p slot="description" class="text-muted">
                A subscription order has a service period, and can also contain one-time items.
                A one-time order is for an order without any service period.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <p class="stack-m">Select order type</p>
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-radio
                                v-for="orderTypeOption in orderTypesOptionsList"
                                class="stack-m"
                                :key="`order-type-list-${orderTypeOption.value}`"
                                :label="orderTypeOption.label"
                                :value="orderTypeOption.value"
                                @update="updateOrderType"
                                :model="order.orderType"
                                group="orderTypeGroup"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Add Products and Plans</h2>
            <p slot="description" class="text-muted">Products and plans form the line items on invoices.</p>
            <div slot="content" class="tile">
                <products-and-plans
                    v-for="(item, index) in order.items"
                    @delete-order-item="deleteOrderItem(index)"
                    @input="updateOrderItem($event, index)"
                    @plan-list="updatePlanList($event)"
                    :key="item.$internalId"
                    :order-item-index="index"
                    :order-item="item"
                    :has-multiple-websites="hasMultipleWebsites"
                    :validate="$v.order.items.$each.$iter[index]"
                    :isDisabled="order.items.length <= 1"
                    :planList="planListCache[item.$productId]"
                    :product-list="productList"/>
                <div class="tile-content">
                    <r-button type="link" @click="addProductAndPlan">Add Another Product and Plan</r-button>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <payment-preferences
            :order="order"
            :coupons="couponList"
            :validate="$v.order"
            @has-trial="hasTrial"
            @autopay="updateAutopay"
            @coupons="updateCoupons"/>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Timing</h2>
            <p slot="description" class="text-muted stack-m">This determines when the period starts (start date), how
                each period will be calculated (anchor date), and when each invoice will be issued (billing timing
                shift) relative to the anchor date’s service calculation.</p>
            <p slot="description" class="text-muted">Items that are billed in advance (prepaid), will be time shifted
                relative to their service period start date, and items that are billed in arrears (postpaid), will be
                shifted relative to their service period end date.</p>
            <template slot="content">
                <div class="tile">
                    <!-- start date form -->
                    <div class="tile-title">
                        <h3>Start Date</h3>
                    </div>
                    <start-date-form
                        :validate="{startTime: $v.startTime, startTimeType: $v.startTimeType}"
                        :start-time-type="startTimeType"
                        :start-time="startTime"
                        @update="updateStartDateTimeOptions"/>

                    <!-- service anchor form -->
                    <div class="tile-title">
                        <h3>Service Anchor Date</h3>
                    </div>
                    <recurring-interval-form
                        description="The anchor date is used to determine the time when service periods change to prepare an invoice."
                        disabled-label="Not available on selected Plan"
                        empty-label="Please select a Product Plan"
                        checkbox-label="Has Service Anchor Shift"
                        :model="PeriodAnchorInstructionModel"
                        :validate="$v.order.recurringInterval.periodAnchorInstruction"
                        :instruction="periodAnchorInstruction"
                        :available-plan-periods="availablePlanPeriods"
                        @update="updateRecurringInterval"/>

                    <div class="tile-title">
                        <h3>Invoice Time Shift</h3>
                    </div>
                    <invoice-time-shift-form
                        description="Once the service period is calculated using the anchor date, the invoice can be issued at that same time, before, or after that date by some shifted period. These settings will override the pricing plan settings."
                        :model="InvoiceTimeShiftModel"
                        :validate="$v.order.invoiceTimeShift"
                        :instruction="order.invoiceTimeShift"
                        @update="updateInvoiceTimeShift"/>
                </div>
            </template>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <r-button class="inline-s"
                          :disabled="isLoading"
                          push="{name: 'customer-details', params: {id: customerId}}">Cancel
                </r-button>
                <r-button type="primary"
                          :loading="isLoading"
                          @click="save">Save Order
                </r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        between, decimal, minValue, numeric, required,
    } from 'vuelidate/lib/validators';
    import _ from '@/common/helpers';
    import ProductModel from '@/common/models/product-model';
    import vTitle from '@/components/v-title';
    import {
        vDateInput,
        vErrorMessages,
        vFormTile,
        vIconButton,
        vRadio,
        vSelect,
    } from '@/components/form';
    import CouponModel from '@/common/models/coupon-model';
    import OrderModel from '@/common/models/order-model';
    import ContactModel from '@/common/models/contact-model';
    import OrderItemModel from '@/common/models/order-item-model';
    import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import ProductsAndPlans from './components/products-and-plans';
    import PaymentPreferences from './components/payment-preferences';
    import RecurringIntervalForm from '@/components/billing-schedule/recurring-interval-form';
    import InvoiceTimeShiftForm from '@/components/billing-schedule/invoice-time-shift-form';
    import StartDateForm from '@/modules/orders/components/start-date-form';
    import getVuelidatePredicates from '@/common/helpers/get-vuelidate-predicates';
    import CustomerModel from '@/common/models/customer-model';
    import InvoiceTimeShiftModel from '@/common/models/invoice-time-shift-model';
    import PeriodAnchorInstructionModel from '@/common/models/period-anchor-instruction-model';

    export default {
        name: 'order-form',
        mixins: [FormLoaderMixin],
        components: {
            StartDateForm,
            InvoiceTimeShiftForm,
            RecurringIntervalForm,
            vTitle,
            vDateInput,
            vFormTile,
            vIconButton,
            vRadio,
            vSelect,
            vErrorMessages,
            ProductsAndPlans,
            PaymentPreferences,
        },
        props: {
            customerId: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                InvoiceTimeShiftModel,
                PeriodAnchorInstructionModel,
                productList: [],
                websiteList: [],
                couponList: [],
                planListCache: {},
                order: new OrderModel({
                    customerId: this.customerId,
                    orderType: OrderModel.Types.subscription,
                }),
                customer: new CustomerModel({id: this.customerId}),
                selectedPlans: [],
                selectedCoupons: [],
                startTimeType: OrderModel.StartTimeTypes.now,
                startTime: null,
            };
        },
        validations() {
            return {
                selectedRecurringIntervals: {
                    unique(values) {
                        return values.length <= 1;
                    },
                },
                selectedTrialPeriods: {
                    unique(values) {
                        return values.length <= 1;
                    },
                },
                selectedCurrencies: {
                    unique(values) {
                        return values.length <= 1;
                    },
                },
                order: {
                    items: {
                        $each: {
                            planId: {required},
                            quantity: {required, decimal, minValue: minValue(1)},
                            $productId: {required},
                        },
                    },
                    recurringInterval: this.getRecurringIntervalValidations(),
                    invoiceTimeShift: this.getInvoiceTimeShiftValidations(),
                },
            };
        },
        computed: {
            isDataRequired() {
                return this.isFormUpdating || !this.isDataReady;
            },
            hasMultipleWebsites() {
                return this.websiteList.length > 1;
            },
            orderTypesOptionsList() {
                return Object.values(OrderModel.Types).map(type => ({
                    value: type,
                    label: _.unKebabCase(type),
                }));
            },
            availablePlanPeriods() {
                return this.selectedRecurringIntervals.map(({unit}) => unit);
            },
            selectedRecurringIntervals() {
                return this.selectedPlans
                    .reduce((result, {recurringInterval}) => {
                        // If already more than one recurring interval, it's enough to throw error.
                        if (result.length > 1) {
                            return result;
                        }

                        // Skip plans w/o recurring interval.
                        if (recurringInterval === null) {
                            return result;
                        }

                        if (result.length === 0) {
                            result.push(recurringInterval);
                            return result;
                        }

                        if (result[0].length !== recurringInterval.length || result[0].unit !== recurringInterval.unit) {
                            result.push(recurringInterval);
                            return result;
                        }

                        return result;
                    }, []);
            },
            selectedTrialPeriods() {
                // User specified order trial, the items periods are ignored.
                if (this.order.trial) {
                    return [];
                }

                return this.selectedPlans
                    .reduce((result, {trial}) => {
                        // If already more than one trial period, it's enough to throw error.
                        if (result.length > 1) {
                            return result;
                        }

                        // Skip plans w/o trial.
                        if (trial === null) {
                            return result;
                        }

                        if (result.length === 0) {
                            result.push(trial.period);
                            return result;
                        }

                        if (result[0].length !== trial.period.length || result[0].unit !== trial.period.unit) {
                            result.push(trial.period);
                            return result;
                        }

                        return result;
                    }, []);
            },
            selectedCurrencies() {
                return this.selectedPlans
                    .reduce((result, {currency}) => {
                        if (currency === null) {
                            return result;
                        }

                        if (!result.includes(currency)) {
                            result.push(currency);
                        }

                        return result;
                    }, []);
            },
            periodAnchorInstruction() {
                if (this.order.recurringInterval) {
                    return this.order.recurringInterval.periodAnchorInstruction;
                }
                return null;
            },
            validationPredicates() {
                // Customize error messages.
                return getVuelidatePredicates(this.$v).map(({rule, predicate, message}) => {
                    const customFieldRuleMessages = {
                        'order.recurringInterval.periodAnchorInstruction.day.required': {
                            message: 'Order service anchor date is required',
                            predicate,
                        },
                        'order.invoiceTimeShift.issueTimeShift.duration.required': {
                            message: 'Invoice issue time shift duration is required',
                            predicate,
                        },
                        'order.items.$each.$iter.planId.required': {
                            message: 'Order item plan is required',
                            predicate,
                        },
                        'order.items.$each.$iter.$productId.required': {
                            message: 'Order item product is required',
                            predicate,
                        },
                        'selectedRecurringIntervals.unique': {
                            message: 'Order items should have same recurring interval',
                            predicate,
                        },
                        'selectedTrialPeriods.unique': {
                            message: 'Order items with trial should have same trial period',
                            predicate,
                        },
                        'selectedCurrencies.unique': {
                            message: 'Order items should have same currencies',
                            predicate,
                        },
                    };
                    return customFieldRuleMessages[rule]
                        || {message, predicate};
                });
            },
        },
        methods: {
            getRecurringIntervalValidations() {
                if (!this.order.recurringInterval) {
                    return {
                        periodAnchorInstruction: {},
                    };
                }
                const {periodAnchorInstruction} = this.order.recurringInterval;
                const validations = {
                    periodAnchorInstruction: {
                        method: {required},
                        day: {},
                        week: {},
                        time: {},
                    },
                };

                switch (periodAnchorInstruction.method) {
                    case PeriodAnchorInstructionModel.PeriodAnchorMethods.dayOfMonth:
                        Object.assign(validations.periodAnchorInstruction, {
                            day: {
                                required,
                                numeric,
                                between: between(1, 31),
                            },
                            time: {},
                        });
                        break;
                    case PeriodAnchorInstructionModel.PeriodAnchorMethods.dayOfWeek:
                        Object.assign(validations.periodAnchorInstruction, {
                            day: {
                                required,
                            },
                            time: {},
                        });
                        break;
                    default:
                        break;
                }
                return validations;
            },
            getInvoiceTimeShiftValidations() {
                if (!this.order.invoiceTimeShift) {
                    return {};
                }
                const validation = {};
                validation.issueTimeShift = !this.order.invoiceTimeShift.issueTimeShift
                    ? {}
                    : {
                        chronology: {
                            required,
                        },
                        duration: {
                            required,
                        },
                        unit: {
                            required,
                        },
                    };
                validation.dueTimeShift = !this.order.invoiceTimeShift.dueTimeShift
                    ? {}
                    : {
                        duration: {
                            required,
                        },
                        unit: {
                            required,
                        },
                    };
                return validation;
            },
            updateOrderItem({product, plan, quantity}, index) {
                const orderItem = {
                    $productId: product && product.id,
                    planId: plan && plan.id,
                    quantity,
                };

                this.order.items.splice(index, 1, {
                    ...this.order.items[index],
                    ...orderItem,
                });

                // Reset recurring interval
                this.updateRecurringInterval(null);

                if (plan !== null) {
                    this.selectedPlans.splice(index, 1, plan);
                } else {
                    this.selectedPlans.splice(index, 1);
                }
            },
            deleteOrderItem(index) {
                this.order.items.splice(index, 1);
                this.selectedPlans.splice(index, 1);
            },
            addProductAndPlan() {
                this.order.items.push(new OrderItemModel());
            },
            async save() {
                this.isLoading = true;
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.isLoading = false;
                    this.scrollTop();
                } else {
                    if (this.startTime) {
                        this.order.startTime = this.startTime.format();
                    }
                    if (this.selectedCoupons.length >= 1) {
                        try {
                            this.selectedCoupons.map(async (redemptionCode) => {
                                const data = {customerId: this.customerId, redemptionCode};
                                await this.$api(({coupons}) => coupons.redeem({data}));
                            });
                        } catch (error) {
                            this.$toast.fromError(error, 'Could not redeem coupons, please try again or contact support');
                        }
                    }
                    try {
                        const {fields} = await this.$api(({subscriptions}) => subscriptions.create({data: this.order.getPayload()}));
                        this.$toast.positive('Order successfully created');
                        this.$router.push({name: 'invoice-details', params: {id: fields.initialInvoiceId}});
                    } catch (error) {
                        this.scrollTop();
                        this.$toast.fromError(error, 'Something went wrong, please contact support');
                    }
                }
                this.isLoading = false;
            },
            updatePlanList(list) {
                if (list.length > 0) {
                    this.planListCache[list[0].productId] = list;
                }
            },
            updateAutopay(value) {
                this.order.autopay = value;
            },
            updateCoupons(value) {
                this.selectedCoupons = value;
            },
            hasTrial(value) {
                this.order.trial = value;
            },
            updateInvoiceTimeShift(invoiceTimeShift) {
                this.order.invoiceTimeShift = new InvoiceTimeShiftModel({...invoiceTimeShift});
            },
            updateRecurringInterval(recurringInterval) {
                this.order.recurringInterval = recurringInterval
                    ? {periodAnchorInstruction: new PeriodAnchorInstructionModel({...recurringInterval})}
                    : null;
            },
            updateStartDateTimeOptions({startTimeType, startTime} = {}) {
                this.startTimeType = startTimeType;
                this.startTime = startTimeType === OrderModel.StartTimeTypes.now
                    ? null
                    : startTime || this.$tz().now().add(1, 'day').startOf('day');
            },
            updateOrderType(orderType) {
                this.order.orderType = orderType;
                if (orderType === OrderModel.Types.oneTime) {
                    this.order.trial = null;
                }
            },
            async fetchData() {
                try {
                    const {fields} = await this.$api(({customers}) => customers.get({id: this.customerId}));
                    this.customer = new CustomerModel({...fields});

                    this.order.deliveryAddress = this.customer.primaryAddress;
                    if (this.customer.defaultPaymentInstrument
                        && this.customer.defaultPaymentInstrument.method === PaymentInstrumentModel.Methods.paymentCard
                    ) {
                        const paymentCard = await this.$api(({paymentCards}) => paymentCards.get({
                            id: this.customer.defaultPaymentInstrument.paymentCardId,
                        }));
                        this.order.billingAddress = new ContactModel(paymentCard.fields.billingAddress);
                    }
                    const [
                        {items: productListItems},
                        {items: websiteListItems},
                        {items: couponListItems},
                    ] = await Promise.all([
                        // TODO: Consider to redo this to load all products.
                        this.$api(({products}) => products.getAll({limit: 100})),
                        this.$api(({websites}) => websites.getAll()),
                        this.$api(({coupons}) => coupons.getAll()),
                    ]);
                    this.productList = productListItems.map(({fields: product}) => new ProductModel(product));
                    this.websiteList = websiteListItems.map(({fields: website}) => website.id);
                    if (this.websiteList.length === 1) {
                        this.order.websiteId = this.websiteList.pop();
                    }
                    this.couponList = couponListItems.map(({fields: coupon}) => new CouponModel(coupon));
                } catch (error) {
                    this.$toast.fromError(error, 'Something went wrong, please contact support');
                }
            },
        },
    };
</script>
